import type { FileIntakeAccept } from './file-intake.types';

/**
 * FileIntakeUtil - I answer what a drag is carrying and which of it the caller will take. ONLY
 * concerned with the files themselves - no events, no state, no rendering.
 */
export const FileIntakeUtil = {
  /**
   * `types` rather than `items`, because a drag's contents are deliberately unreadable until it is
   * dropped - its shape is not. This is what separates a file drag from text dragged out of a
   * paragraph, which is otherwise the same event.
   */
  isFileDrag({ transfer }: { transfer: DataTransfer | null }) {
    return Boolean(transfer?.types.includes('Files'));
  },

  /**
   * Whether a paste is handing over files, or text that happens to travel with a picture of itself.
   *
   * **Text wins, and it has to.** A copy out of a spreadsheet or a document carries the text *and*
   * an image of the selection, and the reader meant the text - taking the image would attach a
   * screenshot of the cells they tried to quote. Only a paste carrying files and no text at all is
   * an attachment: a screenshot, or an image copied from a page.
   *
   * An image copied from a web page carries `text/html` as well, and no `text/plain` - so the html
   * is not consulted. It is only ever the `<img>` the page already showed.
   */
  isFilePaste({ clipboard }: { clipboard: DataTransfer | null }) {
    return clipboard?.types.includes('Files')
      ? clipboard.getData('text/plain') === ''
      : false;
  },

  extensionOf({ fileName }: { fileName: string }) {
    return fileName.includes('.')
      ? fileName.slice(fileName.lastIndexOf('.') + 1)
      : '';
  },

  /**
   * The `accept` attribute's own syntax, so one list can serve a drop and a picker alike: `.ext`
   * matches on the name, `type/*` on the family, anything else on the exact type.
   *
   * A file with no `type` at all is judged on its extension, which is the common case on Windows
   * and the reason a list is usually worth stating both ways.
   */
  isAccepted({ accept, file }: { accept: FileIntakeAccept[]; file: File }) {
    const extension = FileIntakeUtil.extensionOf({
      fileName: file.name,
    }).toLowerCase();
    const type = file.type.toLowerCase();

    return accept.some(accepted => {
      const rule = accepted.toLowerCase();

      if (rule.startsWith('.')) return rule.slice(1) === extension;

      return rule.endsWith('/*')
        ? Boolean(type) && type.startsWith(rule.slice(0, -1))
        : rule === type;
    });
  },

  /**
   * **Rejections come back rather than throwing**, so a drop of ten files with one `.zip` among
   * them yields the nine and the name of the one - which is what a message about it needs.
   *
   * No `accept` accepts everything: a drop cannot be narrowed by the browser the way a picker's
   * dialog can, so the list is the only filter there is.
   *
   * An index loop because `lib` is `ES2022` with no `DOM.Iterable`, so a `FileList` has no iterator
   * to spread, and `item()` is typed as possibly null.
   */
  partition({
    accept,
    files,
  }: {
    accept?: FileIntakeAccept[];
    files: FileList;
  }) {
    const accepted: File[] = [];
    const rejected: string[] = [];

    for (let index = 0; index < files.length; index += 1) {
      const file = files.item(index);

      if (file) {
        if (!accept || FileIntakeUtil.isAccepted({ accept, file })) {
          accepted.push(file);
        } else {
          rejected.push(file.name);
        }
      }
    }

    return { accepted, rejected };
  },
};
